import { useEffect, useRef, useState } from "react";
import { ChatCircleDots, X, PaperPlaneRight, EnvelopeSimple, Phone, CheckCircle, GraduationCap, ArrowLeft } from "@phosphor-icons/react";
import api from "@/lib/api";
import { BRAND_LOGO } from "@/lib/brand";
import DiscoveryCallModal from "@/components/DiscoveryCallModal";
import SamMessage from "@/components/SamMessage";

const LEAD_TRIGGER_PHRASES = ["support@puppl.app", "personally reviewed by the team"];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: "Hi, I'm Sam, the virtual receptionist here at Puppl. I can help with enquiries, bookings, class info and general admin questions. What can I help you with today?" },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [showLead, setShowLead] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", email: "", phone: "", suburb: "", dog_age: "" });
  const [leadSending, setLeadSending] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const [showDiscovery, setShowDiscovery] = useState(false);
  const [showEnrol, setShowEnrol] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    let sid = localStorage.getItem("pp_chat_sid");
    if (!sid) {
      sid = crypto.randomUUID();
      localStorage.setItem("pp_chat_sid", sid);
    }
    setSessionId(sid);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const r = await api.post("/chat/message", { session_id: sessionId, message: text });
      setMsgs((m) => [...m, { role: "assistant", content: r.data.reply }]);
      // If reply hands off to email/private-training, offer lead capture
      if (!leadSent && LEAD_TRIGGER_PHRASES.some((p) => r.data.reply.toLowerCase().includes(p.toLowerCase()))) {
        setShowLead(true);
      }
    } catch {
      setMsgs((m) => [...m, { role: "assistant", content: "Hmm, my leash got tangled. Try again?" }]);
    } finally {
      setSending(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setLeadSending(true);
    try {
      const lastUserMsg = [...msgs].reverse().find((m) => m.role === "user")?.content || "";
      await api.post("/leads", {
        name: leadForm.name,
        email: leadForm.email,
        phone: leadForm.phone,
        suburb: leadForm.suburb,
        dog_age: leadForm.dog_age,
        enquiry: (lastUserMsg || "Chat enquiry") + (leadForm.sms_consent ? "\n\n[Customer consents to SMS contact]" : ""),
        source: "chat_widget",
        session_id: sessionId,
      });
      setLeadSent(true);
      setShowLead(false);
      setMsgs((m) => [
        ...m,
        { role: "assistant", content: `Thanks ${leadForm.name.split(" ")[0]} — the team has your details and will be in touch shortly. Is there anything else I can help with?` },
      ]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", content: "Sorry, that didn't save. Please email support@puppl.app directly." }]);
    } finally {
      setLeadSending(false);
    }
  };

  const onEnrolSuccess = (info) => {
    setShowEnrol(false);
    setMsgs((m) => [...m, {
      role: "assistant",
      content: `All sorted — I've emailed you the invoice for ${info.item_title} (A$${info.amount_aud.toFixed(2)} AUD). Open the link in your inbox to pay and lock in the spot. Anything else I can help with?`,
    }]);
  };

  return (
    <>
      {!open && (
        <div
          className="fixed right-4 sm:right-6 z-30 flex items-center gap-2"
          style={{ bottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))" }}
        >
          <button
            onClick={() => setOpen(true)}
            className="bg-white text-[#1b4a76] rounded-full px-3.5 py-2 text-sm font-bold shadow-lg border-2 border-[#1b4a76] hover:scale-105 transition-transform"
            style={{ animation: "pp-bob 3s ease-in-out infinite" }}
            data-testid="chat-widget-label"
            aria-label="Open chat"
          >
            Questions? Ask Sam
          </button>
          <button
            data-testid="chat-widget-toggle"
            onClick={() => setOpen(true)}
            className="relative bg-[#f38838] hover:bg-[#e87724] text-white rounded-full p-4 shadow-2xl bounce-soft transition-transform hover:scale-105"
            aria-label="Open chat"
          >
            <ChatCircleDots size={26} weight="duotone" />
            <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-400 border-[2.5px] border-white rounded-full animate-pulse" />
          </button>
          <style>{`@keyframes pp-bob { 0%, 100% { transform: translateY(0) } 50% { transform: translateY(-3px) } }`}</style>
        </div>
      )}
      {open && (
        <div data-testid="chat-widget-panel" className="fixed right-4 sm:right-6 z-[80] w-[92vw] max-w-sm rounded-2xl shadow-2xl overflow-hidden border border-stone-200 flex flex-col bg-white" style={{ bottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))", height: "min(34rem, calc(100vh - 6rem))" }}>
          <div className="bg-[#1b4a76] text-[#FDFBF7] px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src={BRAND_LOGO} alt="" className="h-10 w-auto bg-white rounded-md p-0.5" />
              <div>
                <div className="font-heading font-semibold text-lg leading-tight">Chat with Sam</div>
                <div className="text-xs opacity-75">Bookings • enquiries • admin help</div>
              </div>
            </div>
            <button data-testid="chat-widget-close" onClick={() => setOpen(false)} className="hover:opacity-75">
              <X size={20} />
            </button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FDFBF7]">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button onClick={() => setShowDiscovery(true)} className="bg-[#f38838]/10 hover:bg-[#f38838]/15 border border-[#f38838]/30 rounded-xl px-3 py-2.5 flex items-center gap-2 text-left transition-colors" data-testid="chat-discovery-cta">
                <div className="w-8 h-8 rounded-full bg-[#f38838] text-white flex items-center justify-center flex-shrink-0">
                  <Phone size={14} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold text-xs text-stone-900 leading-tight">Free 15-min discovery call</div>
                </div>
              </button>
              <button onClick={() => setShowEnrol(true)} className="bg-[#1b4a76]/5 hover:bg-[#1b4a76]/10 border border-[#1b4a76]/25 rounded-xl px-3 py-2.5 flex items-center gap-2 text-left transition-colors" data-testid="chat-enrol-cta">
                <div className="w-8 h-8 rounded-full bg-[#1b4a76] text-white flex items-center justify-center flex-shrink-0">
                  <GraduationCap size={14} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold text-xs text-stone-900 leading-tight">Sign up for a class or course</div>
                </div>
              </button>
            </div>
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#F3F0E6] text-stone-800 rounded-br-sm"
                      : "bg-white border border-stone-200 text-stone-800 rounded-bl-sm shadow-sm"
                  }`}
                >
                  {m.role === "assistant" ? <SamMessage text={m.content} /> : m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="bg-white border border-stone-200 rounded-2xl px-4 py-2.5 text-sm text-stone-500 inline-flex items-center gap-1" aria-label="Typing">
                  <span className="w-1.5 h-1.5 rounded-full bg-stone-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-stone-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-stone-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
            {showLead && !leadSent && (
              <div className="bg-white border-2 border-[#f38838]/30 rounded-2xl p-4 fade-up" data-testid="chat-lead-form">
                <div className="flex items-center gap-2 mb-2">
                  <EnvelopeSimple size={18} weight="duotone" className="text-[#f38838]" />
                  <div className="font-heading font-semibold text-sm text-stone-900">Quick option — share your details here</div>
                </div>
                <p className="text-xs text-stone-600 mb-3">Save the email step — pop your details below and the team will get in touch.</p>
                <form onSubmit={submitLead} className="space-y-2">
                  <input
                    data-testid="lead-name"
                    type="text"
                    required
                    placeholder="Your name"
                    value={leadForm.name}
                    onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                    className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#f38838]/30"
                  />
                  <input
                    data-testid="lead-email"
                    type="email"
                    required
                    placeholder="Email"
                    value={leadForm.email}
                    onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                    className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#f38838]/30"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      data-testid="lead-suburb"
                      type="text"
                      placeholder="Suburb"
                      value={leadForm.suburb}
                      onChange={(e) => setLeadForm({ ...leadForm, suburb: e.target.value })}
                      className="rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#f38838]/30"
                    />
                    <input
                      data-testid="lead-dog-age"
                      type="text"
                      placeholder="Dog's age"
                      value={leadForm.dog_age}
                      onChange={(e) => setLeadForm({ ...leadForm, dog_age: e.target.value })}
                      className="rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#f38838]/30"
                    />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      data-testid="lead-submit"
                      type="submit"
                      disabled={leadSending}
                      className="bg-[#f38838] hover:bg-[#d97324] disabled:opacity-50 text-white rounded-lg px-3 py-2 text-sm font-semibold flex-1 inline-flex items-center justify-center gap-1.5"
                    >
                      {leadSending ? "Sending..." : (<><CheckCircle size={14} weight="bold" /> Send to the team</>)}
                    </button>
                    <button
                      data-testid="lead-dismiss"
                      type="button"
                      onClick={() => setShowLead(false)}
                      className="text-xs text-stone-500 hover:text-stone-700 px-2"
                    >
                      No thanks
                    </button>
                  </div>
                  <label className="flex items-start gap-1.5 text-[11px] text-stone-600 cursor-pointer pt-1 leading-tight">
                    <input
                      data-testid="lead-sms-consent"
                      type="checkbox"
                      checked={leadForm.sms_consent}
                      onChange={(e) => setLeadForm({ ...leadForm, sms_consent: e.target.checked })}
                      className="mt-0.5 w-3.5 h-3.5 accent-[#f38838] flex-shrink-0"
                    />
                    <span>OK to contact me by SMS too (optional, opt out anytime).</span>
                  </label>
                </form>
              </div>
            )}
          </div>
          <div className="border-t border-stone-200 p-3 bg-white">
            <div className="flex gap-2">
              <input
                data-testid="chat-widget-input"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about classes, bookings or training..."
                className="flex-1 rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#f38838]/30"
              />
              <button
                data-testid="chat-widget-send"
                onClick={send}
                disabled={sending}
                className="bg-[#f38838] hover:bg-[#d97324] disabled:opacity-50 text-white rounded-lg px-3 transition-colors"
              >
                <PaperPlaneRight size={18} weight="bold" />
              </button>
            </div>
            <div className="text-[10px] text-stone-400 text-center mt-2 leading-tight" data-testid="chat-widget-footer">
              Powered by Puppl — your data is private.
            </div>
          </div>
        </div>
      )}
      <DiscoveryCallModal open={showDiscovery} onClose={() => setShowDiscovery(false)} />
      <EnrolModal
        open={showEnrol}
        onClose={() => setShowEnrol(false)}
        sessionId={sessionId}
        onSuccess={onEnrolSuccess}
      />
    </>
  );
}

// ---------- Enrol-and-invoice modal ----------
function EnrolModal({ open, onClose, sessionId, onSuccess }) {
  const [step, setStep] = useState(1); // 1 = pick kind/item, 2 = details
  const [kind, setKind] = useState("class"); // class | course
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [classSessionId, setClassSessionId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", suburb: "", dog_name: "", dog_dob: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setError("");
    setDone(null);
    setClassSessionId("");
    setCourseId("");
    setLoading(true);
    Promise.all([
      api.get("/class-sessions", { params: { upcoming_only: true } }).then((r) => r.data).catch(() => []),
      api.get("/courses").then((r) => r.data).catch(() => []),
    ]).then(([cs, co]) => {
      setClasses(cs.filter((s) => (s.enrolled_count || 0) < (s.capacity || 6) && Number(s.price) > 0));
      setCourses(co.filter((c) => Number(c.price) > 0));
    }).finally(() => setLoading(false));
  }, [open]);

  const selectedTitle = (() => {
    if (kind === "class") {
      const s = classes.find((c) => c.id === classSessionId);
      if (!s) return "";
      const starts = s.starts_at ? new Date(s.starts_at).toLocaleString("en-AU", { timeZone: "Australia/Brisbane", dateStyle: "medium", timeStyle: "short" }) : "";
      return `${s.title || "Class"} — ${starts}`;
    }
    return courses.find((c) => c.id === courseId)?.title || "";
  })();

  const selectedPrice = (() => {
    if (kind === "class") return Number(classes.find((c) => c.id === classSessionId)?.price || 0);
    return Number(courses.find((c) => c.id === courseId)?.price || 0);
  })();

  const canContinue = kind === "class" ? !!classSessionId : !!courseId;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email) { setError("Name and email are required."); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/chat/enrol-and-invoice", {
        kind,
        class_session_id: kind === "class" ? classSessionId : null,
        course_id: kind === "course" ? courseId : null,
        name: form.name,
        email: form.email,
        phone: form.phone,
        suburb: form.suburb,
        dog_name: form.dog_name,
        dog_dob: form.dog_dob,
        notes: form.notes,
        session_id: sessionId,
        origin_url: window.location.origin,
      });
      setDone(r.data);
      onSuccess?.(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't create invoice. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[90] bg-stone-900/70 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="chat-enrol-modal">
      <div className="bg-white rounded-2xl max-w-md w-full my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-stone-900 inline-flex items-center gap-2">
            <GraduationCap size={20} weight="duotone" className="text-[#1b4a76]" />
            {done ? "Invoice sent!" : "Sign up & get your invoice"}
          </h3>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-700" data-testid="chat-enrol-close"><X size={18} /></button>
        </div>

        {done ? (
          <div className="p-5 space-y-3">
            <p className="text-sm text-stone-700">
              We've emailed <strong>{form.email}</strong> an invoice for <strong>{done.item_title}</strong>
              {" "}(A${done.amount_aud.toFixed(2)} AUD).
            </p>
            <p className="text-sm text-stone-700">
              Click the button in that email to pay and lock in the spot. Your place is held only once payment clears.
            </p>
            <a href={done.invoice_url} target="_blank" rel="noopener noreferrer" data-testid="chat-enrol-paynow" className="block text-center bg-[#f38838] hover:bg-[#d97324] text-white font-semibold rounded-lg py-2.5 text-sm">Pay now in your browser</a>
            <button onClick={onClose} className="w-full text-sm text-stone-500 hover:text-stone-700 py-1.5">Close</button>
          </div>
        ) : step === 1 ? (
          <div className="p-5 space-y-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2">What would you like to sign up for?</div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setKind("class")} className={`text-left px-3 py-2 rounded-lg border-2 text-sm transition-colors ${kind === "class" ? "border-[#1b4a76] bg-[#1b4a76]/5 text-[#1b4a76]" : "border-stone-200 text-stone-600 hover:border-stone-300"}`} data-testid="chat-enrol-kind-class">
                  <div className="font-bold">In-person class</div>
                  <div className="text-[11px] text-stone-500">Mudjimba, Kawana &amp; Eudlo</div>
                </button>
                <button onClick={() => setKind("course")} className={`text-left px-3 py-2 rounded-lg border-2 text-sm transition-colors ${kind === "course" ? "border-[#1b4a76] bg-[#1b4a76]/5 text-[#1b4a76]" : "border-stone-200 text-stone-600 hover:border-stone-300"}`} data-testid="chat-enrol-kind-course">
                  <div className="font-bold">Online course</div>
                  <div className="text-[11px] text-stone-500">Self-paced, anywhere</div>
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center text-sm text-stone-500 py-6">Loading available options…</div>
            ) : kind === "class" ? (
              classes.length === 0 ? (
                <div className="text-center text-sm text-stone-500 py-6">No upcoming classes have spots right now. Try an online course or book a discovery call.</div>
              ) : (
                <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1" data-testid="chat-enrol-class-list">
                  {classes.map((s) => {
                    const starts = s.starts_at ? new Date(s.starts_at).toLocaleString("en-AU", { timeZone: "Australia/Brisbane", weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "";
                    return (
                      <button key={s.id} onClick={() => setClassSessionId(s.id)} className={`w-full text-left p-2.5 rounded-lg border text-sm transition-colors ${classSessionId === s.id ? "border-[#f38838] bg-[#f38838]/5" : "border-stone-200 hover:border-stone-300"}`} data-testid={`chat-enrol-class-${s.id}`}>
                        <div className="font-semibold text-stone-900 line-clamp-1">{s.title || "Class"}</div>
                        <div className="text-xs text-stone-500">{starts} {s.location ? `· ${s.location}` : ""}</div>
                        <div className="text-xs font-bold text-[#1b4a76] mt-0.5">A${Number(s.price).toFixed(2)} AUD</div>
                      </button>
                    );
                  })}
                </div>
              )
            ) : (
              courses.length === 0 ? (
                <div className="text-center text-sm text-stone-500 py-6">No online courses are available right now.</div>
              ) : (
                <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1" data-testid="chat-enrol-course-list">
                  {courses.map((c) => (
                    <button key={c.id} onClick={() => setCourseId(c.id)} className={`w-full text-left p-2.5 rounded-lg border text-sm transition-colors ${courseId === c.id ? "border-[#f38838] bg-[#f38838]/5" : "border-stone-200 hover:border-stone-300"}`} data-testid={`chat-enrol-course-${c.id}`}>
                      <div className="font-semibold text-stone-900 line-clamp-1">{c.title}</div>
                      {c.description && <div className="text-xs text-stone-500 line-clamp-2">{c.description}</div>}
                      <div className="text-xs font-bold text-[#1b4a76] mt-0.5">A${Number(c.price).toFixed(2)} AUD</div>
                    </button>
                  ))}
                </div>
              )
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="text-sm text-stone-500 hover:text-stone-700 px-3 py-2">Cancel</button>
              <button onClick={() => setStep(2)} disabled={!canContinue} className="bg-[#1b4a76] hover:bg-[#143a5e] disabled:opacity-40 text-white font-semibold rounded-lg px-4 py-2 text-sm" data-testid="chat-enrol-continue">Continue</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3">
            <button type="button" onClick={() => setStep(1)} className="text-xs text-stone-500 hover:text-stone-700 inline-flex items-center gap-1 mb-1"><ArrowLeft size={12} /> Back</button>
            <div className="bg-[#f38838]/10 border border-[#f38838]/30 rounded-lg px-3 py-2 text-xs">
              <div className="font-semibold text-stone-900">{selectedTitle}</div>
              <div className="text-stone-600">A${selectedPrice.toFixed(2)} AUD</div>
            </div>
            <input required type="text" placeholder="Your full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid="chat-enrol-name" />
            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid="chat-enrol-email" />
            <div className="grid grid-cols-2 gap-2">
              <input type="tel" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid="chat-enrol-phone" />
              <input type="text" placeholder="Suburb" value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} className="rounded-lg border border-stone-200 px-3 py-2 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input type="text" placeholder="Dog's name" value={form.dog_name} onChange={(e) => setForm({ ...form, dog_name: e.target.value })} className="rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid="chat-enrol-dog-name" />
              <input type="date" placeholder="Dog's DOB" value={form.dog_dob} onChange={(e) => setForm({ ...form, dog_dob: e.target.value })} className="rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid="chat-enrol-dog-dob" />
            </div>
            <textarea rows={2} placeholder="Anything the team should know? (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" />
            {error && <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
            <p className="text-[11px] text-stone-500 leading-tight">We'll email you a secure Stripe invoice. Your spot is only held once payment clears.</p>
            <button type="submit" disabled={submitting} className="w-full bg-[#f38838] hover:bg-[#d97324] disabled:opacity-50 text-white font-semibold rounded-lg py-2.5 text-sm inline-flex items-center justify-center gap-2" data-testid="chat-enrol-submit">
              {submitting ? "Sending invoice…" : (<><EnvelopeSimple size={14} weight="bold" /> Email me the invoice</>)}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
